import * as Sentry from '@sentry/react'
import type { IOrder, IPrimer } from '../../Model/index.js'
import { getTelemetryOrg, trackDistribution, trackMetric } from './Telemetry.js'

/**
 * Order/visualisation telemetry helpers. All of these resolve the current user's role w.r.t. the
 * order (customer vs lab) automatically, so every order metric is segmentable by role.
 */

/** The user's relationship to a given order, derived from the current org. */
export type OrderRole = 'customer' | 'lab' | 'other'

export const resolveOrderRole = (order: Pick<IOrder, 'lab' | 'customer'>): OrderRole => {
  const orgId = getTelemetryOrg()?.id
  if (orgId && orgId === order.lab) return 'lab'
  if (orgId && orgId === order.customer) return 'customer'
  return 'other'
}

/**
 * Every actionable button in the order view (except the visualisation openers, which use
 * {@link trackVizOpened}). Add new buttons by extending this union — NOT by adding new metric names.
 */
export type OrderAction =
  | 'submit'
  | 'view_quality'
  | 'start_postprocessing'
  | 'claim_lab'
  | 'claim_customer'
  | 'share'
  | 'copy_share_link'
  | 'completion_notifications'
  | 'invite_lab'
  | 'download_fastq'
  | 'download_report'
  /** Opened the report-settings popup (SPIN etc.) rather than taking the one-click plain report. */
  | 'download_report_custom'
  | 'download_multiqc'
  | 'download_otu_fasta'
  | 'download_summary_pdf'
  | 'print_summary'
  | 'edit_order'
  | 'delete_order'
  | 'pullback'
  | 'rerun_preprocessing'
  | 'reprocess_order'
  | 'import_samples'
  | 'manage_files'
  | 'upload_csv'
  | 'edit_data_beta'
  | 'download_pool_csv'
  | 'add_sample_pool'
  | 'delete_sample_pool'
  | 'convert_pool_item'
  | 'delete_pool_item'
  | 'update_sample_data'
  | 'update_sample_metadata'

/** A button click in the order view (intent). */
export const trackOrderAction = (action: OrderAction, order: Pick<IOrder, 'id' | 'lab' | 'customer' | 'processingType'>): void => {
  trackMetric('order_view.action', {
    action,
    role: resolveOrderRole(order),
    order_id: order.id,
    processing_type: order.processingType
  })
}

/**
 * An option chosen in the order-creation wizard (before the order exists, so no order role).
 * `optionType` e.g. taxon/region/processing_type/sample_configuration/redlist_mode/singletons;
 * `mode` is lab vs customer. Answers "which order shapes do users build" behaviourally.
 *
 * The chosen option is sent as `option_value`, NOT `value`: every metric row already has a numeric
 * `value` column, so an attribute of that name is unreachable — grouping by `value` returns the
 * counter's own 1s and the selection is invisible.
 */
export const trackOrderCreationOption = (
  optionType: string,
  value: string,
  mode: string,
  extra: Record<string, string | number | boolean> = {}
): void => {
  trackMetric('order_creation.option_selected', { option_type: optionType, option_value: value, mode, ...extra })
}

/**
 * Order editing: diff the order before/after a successful edit and emit one `order.edited` per
 * changed field, so "how often orders are edited" = count and "what was edited" = group by `field`.
 */
const EDITABLE_FIELDS: Array<{ key: keyof IOrder; field: string }> = [
  { key: 'name', field: 'name' },
  { key: 'description', field: 'description' },
  { key: 'targetTaxonomy', field: 'taxonomy' },
  { key: 'targetRegion', field: 'region' },
  { key: 'allowSingletons', field: 'singletons' },
  { key: 'processingType', field: 'processing_type' },
  { key: 'redlistMode', field: 'redlist_mode' },
  { key: 'sampleConfiguration', field: 'sample_configuration' }
]

export const trackOrderEdited = (previous: IOrder, next: IOrder): void => {
  for (const { key, field } of EDITABLE_FIELDS) {
    if (previous[key] !== next[key]) {
      trackMetric('order.edited', { field, role: resolveOrderRole(next), order_id: next.id, processing_type: next.processingType })
    }
  }
}

/**
 * Why a submit click ended without the SubmitOrder modal opening, across the widget
 * (`validatePrimersAndShowSubmit`) and the epic it dispatches into
 * (`UpdatePrimersForSubmitEpic`), so the drop-off between `order_view.action` `submit` and
 * `order.lifecycle` `submitted` can be attributed instead of just observed.
 */
export type SubmitBlockedReason =
  | 'not_lab'
  | 'primer_not_found'
  | 'amplicon_mismatch'
  | 'validation_error'
  | 'primer_null'
  | 'primer_ambiguous'
  | 'platform_unknown'
  | 'pool_update_failed'

export const trackSubmitBlocked = (
  reason: SubmitBlockedReason,
  order: Pick<IOrder, 'id' | 'lab' | 'customer' | 'processingType'>
): void => {
  trackMetric('order.submit_blocked', {
    reason,
    role: resolveOrderRole(order),
    order_id: order.id,
    processing_type: order.processingType
  })
}

/** Tells us a registry primer collision needs cleaning up; the lab's pick has already unblocked the order. */
export const reportPrimerChoice = (
  order: { id: string; targetTaxonomy?: string },
  candidates: IPrimer[],
  chosen: IPrimer,
  flow: 'classic' | 'wizard'
): void => {
  const org = getTelemetryOrg()
  try {
    Sentry.captureMessage('Ambiguous registry primer chosen by the lab', {
      level: 'warning',
      tags: { primer_collision: flow, order_id: order.id, ...(org ? { org_id: org.id, is_internal: String(org.isInternal) } : {}) },
      contexts: {
        primer_collision: {
          amplicon: order.targetTaxonomy,
          chosen: `${chosen.id} ${chosen.forwardName}/${chosen.reverseName}`,
          candidates: candidates.map((p) => `${p.id} ${p.forwardName}/${p.reverseName} ${p.forwardSequence}/${p.reverseSequence}`)
        }
      }
    })
  } catch {
    /* telemetry must never block a submit */
  }
}

/** Confirmed order state transitions (fired on success, used for conversion ratios). */
export type OrderLifecycleStage = 'created' | 'draft_created' | 'submitted' | 'claimed' | 'postprocessing_started' | 'deleted'

export const trackOrderLifecycle = (
  stage: OrderLifecycleStage,
  order: Pick<IOrder, 'id' | 'lab' | 'customer' | 'processingType'>
): void => {
  trackMetric('order.lifecycle', {
    stage,
    role: resolveOrderRole(order),
    order_id: order.id,
    processing_type: order.processingType
  })
}

/**
 * How a report download resolved, server-side:
 *  - `fresh_generation`: the report didn't exist / was stale, so generation was triggered (HTTP 202).
 *  - `direct`: the report was ready and served immediately (HTTP 200).
 */
export type ReportDownloadKind = 'fresh_generation' | 'direct'

export const trackReportDownload = (
  kind: ReportDownloadKind,
  order: { id: string; lab?: string; customer?: string; processingType?: string }
): void => {
  trackMetric('report.download', {
    kind,
    role: resolveOrderRole(order),
    order_id: order.id,
    processing_type: order.processingType
  })
}

/** Which visualisation surface the user opened. */
export type VizChartType = 'otu_table' | 'krona' | 'workspace'

/**
 * How the surface was reached. `menu` is a click on an opener in the order context actions;
 * `navigation` is arriving at the route itself (a link, a bookmark, the back button). Order-scoped
 * opens are counted at the menu and therefore undercount direct navigation, project-scoped opens
 * are counted on arrival — without this attribute the two look comparable and are not.
 */
export type VizOpenTrigger = 'menu' | 'navigation'

export const trackVizOpened = (chartType: VizChartType, order: Pick<IOrder, 'id' | 'lab' | 'customer' | 'processingType'>): void => {
  trackMetric('viz.opened', {
    chart_type: chartType,
    scope: 'order',
    trigger: 'menu' satisfies VizOpenTrigger,
    role: resolveOrderRole(order),
    order_id: order.id,
    processing_type: order.processingType
  })
}

/**
 * A user applying a saved view/template in the visualisation workspace. The "default" is the
 * untouched workspace (no template applied), so this firing at all is the signal that someone went
 * beyond the default. `isDefault` is here for completeness if a real default-template concept lands.
 */
export const trackVizTemplateSelected = (
  templateId: string,
  isDefault: boolean,
  order?: Pick<IOrder, 'id' | 'lab' | 'customer'>,
  extra: Record<string, string | number | boolean> = {}
): void => {
  trackMetric('viz.template_selected', {
    template_id: templateId,
    is_default: isDefault,
    role: order ? resolveOrderRole(order) : undefined,
    order_id: order?.id,
    ...extra
  })
}

// ---------------------------------------------------------------------------
// Engagement & revisit tracking
// ---------------------------------------------------------------------------

/**
 * Fired when the order detail page is mounted.
 * `reason` distinguishes a first visit (`initial`) from a revisit (`reopen`)
 * so we can measure order revisit frequency.
 */
export type PageViewReason = 'initial' | 'reopen'

export const trackOrderPageView = (
  order: Pick<IOrder, 'id' | 'lab' | 'customer' | 'processingType'>,
  reason: PageViewReason
): void => {
  trackMetric('order.page_view', {
    reason,
    role: resolveOrderRole(order),
    order_id: order.id,
    processing_type: order.processingType
  })
}

/**
 * Longest dwell we treat as one sitting. Beyond this the reading is a tab left open, not attention:
 * uncapped, this distribution had a p95 of ~2.4 hours and single samples over 17 hours, which made
 * every percentile above the median useless. Samples that hit the ceiling carry `capped: true`, so
 * they stay countable instead of silently distorting the shape.
 */
const DWELL_CAP_SECONDS = 30 * 60

/**
 * Time the user actually spent looking at the order detail page — visible time only, from mount to
 * unmount, with hidden stretches excluded and the total clamped to {@link DWELL_CAP_SECONDS}.
 */
export const trackOrderDwellTime = (
  durationSeconds: number,
  order: Pick<IOrder, 'id' | 'lab' | 'customer' | 'processingType'>
): void => {
  const capped = durationSeconds > DWELL_CAP_SECONDS
  trackDistribution('order.dwell_time', capped ? DWELL_CAP_SECONDS : durationSeconds, {
    role: resolveOrderRole(order),
    order_id: order.id,
    capped
  }, 'second')
}

/**
 * The order list / dashboard page finished a fetch. A single per-order `role` doesn't apply (the
 * list mixes customer- and lab-side orders), so instead we emit the *role split* of the visible
 * page: how many of the orders this user is browsing they are the customer vs the lab on. Grouped
 * over time this is the behavioural "is this user mostly a customer" signal — it complements the
 * org's own `org_type` classification carried on every metric.
 *
 * `total_count` is the grand total (all pages); the split is over the current page only.
 */
export type ListSourceType = 'initial' | 'navigate' | 'refresh'

export const trackOrderListView = (
  orders: Array<Pick<IOrder, 'lab' | 'customer'>>,
  totalOrders: number,
  source: ListSourceType = 'initial'
): void => {
  let customer = 0
  let lab = 0
  let other = 0
  for (const order of orders) {
    const role = resolveOrderRole(order)
    if (role === 'customer') customer++
    else if (role === 'lab') lab++
    else other++
  }
  trackMetric('order.list_view', {
    total_count: String(totalOrders),
    page_count: String(orders.length),
    customer_count: String(customer),
    lab_count: String(lab),
    other_count: String(other),
    source
  })
}

/**
 * A section (tab / collapsible panel) on the order detail page was expanded or collapsed.
 * `section` names: `metadata`, `pool-N` (N = pool index), `all` (expand/collapse all).
 * `pool_index` is the numeric index for `pool-N` sections (useful for grouping).
 */
export type SectionAction = 'expand' | 'collapse'

export const trackOrderSection = (
  section: string,
  action: SectionAction,
  poolIndex?: number
): void => {
  trackMetric('order.section', {
    section,
    action,
    ...(poolIndex !== undefined ? { pool_index: String(poolIndex) } : {})
  })
}

/**
 * The user switched tabs within the order detail page (e.g. between Metadata, Pool 0, Pool 1…).
 */
export const trackOrderTabSwitch = (
  section: string
): void => {
  trackMetric('order.tab_switch', {
    section
  })
}
